import getGuestCartQuery from '~layers/app/graphql/cart/getGuestCart.gql'
import getCustomerCartQuery from '~layers/app/graphql/cart/getCustomerCart.gql'
import createEmptyCartMutation from '~layers/app/graphql/cart/createEmptyCart.gql'
import addConfigurableProductsToCart from '~layers/app/graphql/cart/addConfigurableProductsToCart.gql'
import addSimpleProductsToCart from '~layers/app/graphql/cart/addSimpleProductsToCart.gql'
import updateCartItemsMutation from '~layers/app/graphql/cart/updateCartItems.gql'
import removeItemFromCartMutation from '~layers/app/graphql/cart/removeItemFromCart.gql'
import applyCouponToCartMutation from '~layers/app/graphql/cart/applyCouponToCart.gql'
import setGuestEmailOnCartMutation from '~layers/app/graphql/cart/setGuestEmailOnCart.gql'
import setBillingAddressOnCartMutation from '~layers/app/graphql/cart/setBillingAddressOnCart.gql'
import setShippingAddressesOnCartMutation from '~layers/app/graphql/cart/setShippingAddressesOnCart.gql'
import setShippingMethodsOnCartMutation from '~layers/app/graphql/cart/setShippingMethodsOnCart.gql'
import placeOrderGraphqlMutation from '~layers/app/graphql/cart/placeOrder.gql'
import setPaymentMethodOnCartMutation from '~layers/app/graphql/cart/setPaymentMethodOnCart.gql'
import removeCouponFromCartMutation from '~layers/app/graphql/cart/removeCouponFromCart.gql'
import mergeCartsMutation from '~layers/app/graphql/cart/mergeCarts.gql'
import restoreCartMutationQuery from '~layers/app/graphql/cart/restoreCart.gql'
import cartStatusQuery from '~layers/app/graphql/cart/cartStatus.gql'

import type {
  MagentoBillingAddress,
  MagentoCart, MagentoCartItem,
  MagentoCartItemInput,
  MagentoCartItemUpdateInput,
  MagentoPaymentMethod,
  MagentoPaymentMethodInput,
  MagentoSetBillingAddressOnCartInput,
  MagentoSetShippingAddressOnCartInput,
  MagentoShippingAddress,
  MagentoShippingMethod,
  MagentoProduct,
} from '~layers/app/types/magento'

// const cart = ref<MagentoCart>()
// const cartId = ref<string | null>(null)
// const addedToCart = ref<Record<string, number>>({})
// const pending = ref<boolean>(false)
// const inactive = ref<boolean>(false)
// const watchersEnabled = ref<boolean>(false)
// const loaded = ref<boolean>(false)
// const errors = ref<string[]>([])
// const errorsToFailOn:string[] = [
//   'The current user cannot perform operations on cart',
//   'Could not find a cart with ID',
//   'Current user does not have an active cart',
//   'The cart isn\'t active',
//   'Kan geen winkelwagen vinden met ID',
//   'Huidige klant heeft geen actieve winkelwagen',
//   'De huidige gebruiker kan geen bewerkingen uitvoeren op winkelwagen'
// ]

// const errorsToSuppress: string[] = [
//   'Sommige van de producten zijn niet op voorraad',
//   'Some of the products are out of stock',
// ]

export function useCart() {
  const cart = useState<MagentoCart | null>('cart', () => undefined)
  const cartId = useState<string | null>('cartId', () => null)
  const addedToCart = useState<Record<string, number>>('addedToCart', () => {})
  const pending = useState<boolean>('cartPending', () => false)
  const inactive = useState<boolean>('cartInactive', () => false)
  const watchersEnabled = useState<boolean>('cartWatchersEnabled', () => false)
  const loaded = useState<boolean>('cartLoaded', () => false)
  const errors = useState<string[]>('cartErrors', () => [])
  // const errorsToFailOn:string[] = [
  //   'The current user cannot perform operations on cart',
  //   'Could not find a cart with ID',
  //   'Current user does not have an active cart',
  //   'The cart isn\'t active',
  //   'Kan geen winkelwagen vinden met ID',
  //   'Huidige klant heeft geen actieve winkelwagen',
  //   'De huidige gebruiker kan geen bewerkingen uitvoeren op winkelwagen'
  // ]

  const errorsToSuppress: string[] = [
    'Sommige van de producten zijn niet op voorraad',
    'Some of the products are out of stock',
  ]

  const { public: config } = useRuntimeConfig()
  const cartCookieName = 'quote_masked_id_' + config.storeHeader

  const { isLoggedIn, load: loadCustomer } = useAccount()

  const cartCookie = useCookie(cartCookieName, {
    maxAge: 60 * 60 * 24 * 60,
  })

  const placedOrderForQuoteCookie = useCookie('placed_order_for_quote', {
    maxAge: 60 * 60 * 10,
  })

  const setCartId = (token: string) => {
    cartCookie.value = token
    cartId.value = token
  }

  const getCartId = computed(() =>
    cart.value?.id || cartId.value || cartCookie.value,
  )

  const skus = computed<string[]>(() =>
    cart.value?.items.map(item => item.product.sku) || [],
  )

  const items = computed(() =>
    cart.value?.items || [],
  )

  const quantity = computed<number>(() =>
    cart.value?.items?.length || 0,
  )

  const sumQuantity = computed<number>(() =>
    cart.value?.total_quantity || 0,
  )

  const total = computed<number>(() =>
    cart.value?.prices?.grand_total.value || 0,
  )

  const skusWithQuantities = computed<Record<string, number>>(() =>
    cart.value?.items?.reduce((acc: Record<string, number>, item) => {
      const sku = item.configured_variant?.sku || item.product.sku
      // FIX: Sum quantities instead of overwriting for duplicate SKUs (free + paid versions)
      acc[sku] = (acc[sku] || 0) + item.quantity
      return acc
    }, {}) || {},
  )

  const hasSuppressedErrors = computed<boolean>(() =>
    !!errorsToSuppress.filter(item => errors.value.includes(item)).length,
  )

  const getCartItemsWithErrors = computed(() =>
    items.value.filter(item => item.errors),
  )

  const hasCartItemErrors = computed<boolean>(() =>
    !!getCartItemsWithErrors.value.length,
  )

  const shippingCosts = computed<{ label: string, value: number } | null>(() => {
    let shipment = cart.value?.shipping_addresses ? cart.value.shipping_addresses[0] : null

    if (!shipment || !shipment.available_shipping_methods || !shipment.selected_shipping_method) {
      return null
    }
    shipment = shipment.available_shipping_methods.find(method => method.method_code === shipment.selected_shipping_method.method_code)

    if (!shipment) {
      return null
    }

    return {
      label: shipment.method_title,
      value: shipment.price_incl_tax.value,
    }
  })

  const shippingAddress = computed<MagentoShippingAddress | null>(() =>
    cart.value?.shipping_addresses?.[0]?.country?.code ? cart.value?.shipping_addresses[0] : null,
  )

  const billingAddress = computed<MagentoBillingAddress | null>(() =>
    cart.value?.billing_address,
  )

  const shippingAddressOrBillingAddress = computed<MagentoShippingAddress | MagentoBillingAddress | null>(() =>
    shippingAddress.value || billingAddress.value,
  )

  const shippingMethods = computed<MagentoShippingMethod[]>(() => cart.value?.shipping_addresses?.[0]?.available_shipping_methods?.map((method) => {
    const id = method.carrier_code + '_' + method.method_code
    return {
      ...method,
      id,
      type: null,
    }
  }))

  const crossSells = (subselect: MagentoCartItem[] | undefined): MagentoProduct[] => {
    return Object.values(
      (subselect || items.value).reduce((acc: any, item: MagentoCartItem) => {
        return {
          ...acc,
          ...(item.product.crosssell_products || []).reduce((acc2: any, product: MagentoProduct) => {
            return { ...acc2, [product.sku]: product }
          }, {}),
        }
      }, {}),
    )
  }

  const upSells = (subselect: MagentoCartItem[] | undefined): MagentoProduct[] => {
    return Object.values(
      (subselect || items.value).reduce((acc: any, item: MagentoCartItem) => {
        return {
          ...acc,
          ...(item.product.upsell_products || []).reduce((acc2: any, product: MagentoProduct) => {
            return { ...acc2, [product.sku]: product }
          }, {}),
        }
      }, {}),
    )
  }

  const selectedShippingMethod = computed<MagentoShippingMethod | null>(() => {
    const method = cart.value?.shipping_addresses?.[0]?.selected_shipping_method
    if (!method) {
      return null
    }
    const id = method.carrier_code + '_' + method.method_code
    return shippingMethods.value.find(item => item.id === id)
  })

  const selectedPaymentMethod = computed<MagentoPaymentMethod | null>(() =>
    cart.value?.selected_payment_method || null,
  )

  const handleAddedToCart = (compareWith?: Record<string, number>) => {
    if (!compareWith) {
      addedToCart.value = {}
      return
    }
    const diff: Record<string, number> = {}
    for (const i in skusWithQuantities.value) {
      if (!compareWith[i] || compareWith[i] < skusWithQuantities.value[i]) {
        diff[i] = skusWithQuantities.value[i] - (compareWith[i] || 0)
      }
    }
    addedToCart.value = diff
  }

  const handleErrors = (incomingErrors) => {
    if (incomingErrors?.graphQLErrors) {
      errors.value = incomingErrors.graphQLErrors.map(item => item.message)
    }
    else {
      errors.value = []
    }
  }

  const email = computed<string | null>(() =>
    cart.value?.email || null,
  )

  const reset = async () => {
    await loadCustomer()
    if (!isLoggedIn.value) {
      cartId.value = null
      cartCookie.value = null
      cart.value = null
    }
  }

  const getCart = async (options?: { token?: string, force?: boolean }) => {
    if (pending.value) {
      return
    }
    if (cart.value && !options?.force) {
      return
    }

    pending.value = true
    setCartId(options?.token || cartCookie.value || '')
    await loadCustomer()
    if (isLoggedIn.value) {
      await getCustomerCart()
      await mergeCarts()
    }
    else {
      await getGuestCart()
    }
    pending.value = false
  }

  const getGuestCart = async () => {
    if (!cartId.value) {
      cart.value = undefined
      return
    }

    try {
      const { client } = useApolloClient('customer')
      const result = await client.query<any>({
        query: getGuestCartQuery,
        variables: { cartId: cartId.value },
      })
      setCartData(result.data.cart)
    }
    catch (e) {
      if (e.message === 'The cart isn\'t active.') {
        inactive.value = true
      }
      else {
        await createEmptyCart()
      }
    }
  }

  const setCartData = (cartData: any) => {
    cart.value = {
      ...cartData,
    }
  }

  const cartStatus = async (cartId: string) => {
    try {
      const { client } = useApolloClient('customer')
      const result = await client.query<any>({
        query: cartStatusQuery,
        variables: {
          cartId,
        },
      })
      return result.data.cartStatus || null
    }
    catch (e) {
      console.error('FETCH CART STATUS FAILED', e)
      return null
    }
  }

  const getCustomerCart = async () => {
    try {
      const { client } = useApolloClient('customer')
      const result = await client.query<any>({
        query: getCustomerCartQuery,
      })
      setCartData(result.data.cart)

      if (placedOrderForQuoteCookie.value && placedOrderForQuoteCookie.value !== result.data.cart.id) {
        inactive.value = true
      }
    }
    catch (e) {
      console.error('FETCH CUSTOMER CART ERRORS TODO', e)
    }
  }

  const mergeCarts = async () => {
    if (!cartCookie.value) {
      return
    }
    const { mutate, error } = useMutation(mergeCartsMutation, { clientId: 'customer' })

    try {
      const data = await mutate({
        guestCardId: cartCookie.value,
        customerCartId: getCartId.value,
      })
      if (data?.data?.mergeCarts) {
        cartCookie.value = null
        setCartData(data?.data?.mergeCarts)
        handleErrors([])
      }
    }
    catch (e) {
      console.error(e)
      handleErrors(error.value)
    }
  }

  const createEmptyCart = async () => {
    await loadCustomer()
    if (isLoggedIn.value) {
      return
    }
    const { mutate, error } = useMutation(createEmptyCartMutation, { clientId: 'customer' })
    const data = await mutate()
    if (!error.value) {
      cart.value = undefined
      setCartId(data?.data.createEmptyCart)
    }
  }

  const {
    mutate: addSimpleProductsToCartMutation,
    error: addSimpleProductsToCartError,
  } = useMutation(
    addSimpleProductsToCart,
    { clientId: 'customer' },
  )

  const {
    mutate: addConfigurableProductsToCartMutation,
    error: addConfigurableProductsToCartError,
  } = useMutation(
    addConfigurableProductsToCart,
    { clientId: 'customer' },
  )

  const addToCart = async (cartItems: MagentoCartItemInput[], suppress: boolean | undefined) => {
    pending.value = true
    if (!getCartId.value) {
      await createEmptyCart()
    }
    if (!getCartId.value) {
      console.error('No cart id')
      return
    }

    const currentCartContents = { ...skusWithQuantities.value }
    const configurableItemsToAdd = cartItems.filter(item => item.parent_sku)
    const simpleItemsToAdd = cartItems.filter(item => !item.parent_sku)

    const addProductsToCartMutations = []
    if (simpleItemsToAdd.length) {
      addProductsToCartMutations.push(addSimpleProductsToCartMutation({
        input: {
          cart_id: getCartId.value,
          cart_items: simpleItemsToAdd,
        },
      }))
    }
    if (configurableItemsToAdd.length) {
      addProductsToCartMutations.push(addConfigurableProductsToCartMutation({
        input: {
          cart_id: getCartId.value,
          cart_items: configurableItemsToAdd,
        },
      }))
    }

    try {
      const promiseResults = await Promise.all(addProductsToCartMutations)
      const cartData = promiseResults.pop()
      if (cartData?.data?.addToCart) {
        setCartData(cartData?.data?.addToCart?.cart)
        handleAddedToCart(suppress ? undefined : currentCartContents)
        handleErrors([])
      }
    }
    catch {
      handleAddedToCart(suppress ? undefined : currentCartContents)
      handleErrors(addSimpleProductsToCartError.value || addConfigurableProductsToCartError.value)
    }
    pending.value = false
  }

  const updateCartItem = async (cartItem: MagentoCartItemUpdateInput, suppress: boolean | undefined) => {
    if (!getCartId.value) {
      return
    }
    const currentCartContents = { ...skusWithQuantities.value }
    const { mutate, error } = useMutation(updateCartItemsMutation, { clientId: 'customer' })

    try {
      pending.value = true
      const data = await mutate({
        input: {
          cart_id: getCartId.value,
          cart_items: [cartItem],
        },
      })
      if (data?.data?.updateCartItems) {
        setCartData(data?.data?.updateCartItems?.cart)

        handleAddedToCart(suppress ? undefined : currentCartContents)
      }
    }
    catch {
      handleErrors(error.value)
    }
    pending.value = false
  }

  const removeCartItem = async (cartItemId: number) => {
    if (!getCartId.value) {
      return
    }
    const { mutate, error } = useMutation(removeItemFromCartMutation, { clientId: 'customer' })

    try {
      pending.value = true
      const data = await mutate({
        input: {
          cart_id: getCartId.value,
          cart_item_id: cartItemId,
        },
      })
      if (data?.data?.removeItemFromCart) {
        setCartData(data?.data?.removeItemFromCart?.cart)
        handleAddedToCart()
      }
    }
    catch {
      handleErrors(error.value)
    }
    pending.value = false
  }

  const applyCouponToCart = async (couponCode: string) => {
    if (!getCartId.value) {
      return
    }
    const { mutate, error } = useMutation(applyCouponToCartMutation, { clientId: 'customer' })

    try {
      pending.value = true
      const data = await mutate({
        input: {
          cart_id: getCartId.value,
          coupon_code: couponCode,
        },
      })
      if (data?.data?.applyCouponToCart) {
        setCartData(data?.data?.applyCouponToCart?.cart)
        handleErrors([])
      }
    }
    catch {
      handleErrors(error.value)
    }
    pending.value = false
  }

  const removeCouponFromCart = async () => {
    if (!getCartId.value) {
      return
    }
    const { mutate, error } = useMutation(removeCouponFromCartMutation, { clientId: 'customer' })

    try {
      pending.value = true
      const data = await mutate({
        input: {
          cart_id: getCartId.value,
        },
      })
      if (data?.data?.removeCouponFromCart) {
        setCartData(data?.data?.removeCouponFromCart?.cart)
        handleErrors([])
      }
    }
    catch {
      handleErrors(error.value)
    }
    pending.value = false
  }

  const {
    mutate: addEmailAddressMutation,
    loading: addEmailAddressLoading,
    called: addEmailAddressCalled,
    error: addEmailAddressError,
  } = useMutation(
    setGuestEmailOnCartMutation,
    { clientId: 'customer' },
  )

  const addEmailAddress = async (email: string) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true

    try {
      const data = await addEmailAddressMutation({
        input: {
          cart_id: getCartId.value,
          email,
        },
      })
      setCartData(data?.data?.cart?.cart)
    }
    catch (addEmailAddressException: any) {
      console.error('addEmailAddress', addEmailAddressException)
    }
    pending.value = false
  }

  const {
    mutate: addBillingAddressMutation,
    loading: addBillingAddressLoading,
    called: addBillingAddressCalled,
    error: addBillingAddressError,
  } = useMutation(
    setBillingAddressOnCartMutation,
    { clientId: 'customer' },
  )

  const addBillingAddress = async (address: MagentoSetBillingAddressOnCartInput) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true

    try {
      const data = await addBillingAddressMutation({
        input: {
          cart_id: getCartId.value,
          billing_address: address,
        },
      })
      setCartData(data?.data?.cart?.cart)
    }
    catch (addBillingAddressException: any) {
      console.error('addBillingAddressException', addBillingAddressException)
    }
    pending.value = false
  }

  const {
    mutate: addShippingAddressMutation,
    loading: addShippingAddressLoading,
    called: addShippingAddressCalled,
    error: addShippingAddressError,
  } = useMutation(
    setShippingAddressesOnCartMutation,
    { clientId: 'customer' },
  )
  const addShippingAddress = async (address: MagentoSetShippingAddressOnCartInput) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true

    try {
      const data = await addShippingAddressMutation({
        input: {
          cart_id: getCartId.value,
          shipping_addresses: [address],
        },
      })
      setCartData(data?.data?.cart?.cart)
    }
    catch (addShippingAddressException: any) {
      console.error('addShippingAddressException', addShippingAddressException)
    }
    pending.value = false
  }

  const {
    mutate: addShippingMethodMutation,
    loading: addShippingMethodLoading,
    called: addShippingMethodCalled,
    error: addShippingMethodError,
  } = useMutation(
    setShippingMethodsOnCartMutation,
    { clientId: 'customer' },
  )
  const addShippingMethod = async (carrierCode: string, methodCode: string, extraData: any | undefined) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true

    try {
      const data = await addShippingMethodMutation({
        input: {
          cart_id: getCartId.value,
          shipping_methods: [{
            carrier_code: carrierCode,
            method_code: methodCode,
          }],
          ...(extraData !== undefined ? extraData : {}),
        },
      })
      setCartData(data?.data?.cart?.cart)
    }
    catch (addShippingMethodException: any) {
      console.error('addShippingMethodException', addShippingMethodException)
    }

    pending.value = false
  }

  const {
    mutate: addPaymentMethodMutation,
    loading: addPaymentMethodLoading,
    called: addPaymentMethodCalled,
    error: addPaymentMethodError,
  } = useMutation(
    setPaymentMethodOnCartMutation,
    { clientId: 'customer' },
  )
  const addPaymentMethod = async (paymentMethod: MagentoPaymentMethodInput) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true

    try {
      const data = await addPaymentMethodMutation({
        input: {
          cart_id: getCartId.value,
          payment_method: paymentMethod,
        },
      })
      setCartData(data?.data?.cart?.cart)
    }
    catch (addPaymentMethodException: any) {
      console.error('addPaymentMethodException', addPaymentMethodException)
    }

    pending.value = false
  }

  const {
    mutate: placeOrderMutation,
    loading: placeOrderLoading,
    called: placeOrderCalled,
    error: placeOrderError,
  } = useMutation(
    placeOrderGraphqlMutation,
    { clientId: 'customer' },
  )

  const placeOrder = async () => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true

    try {
      const data = await placeOrderMutation({
        input: {
          cart_id: getCartId.value,
        },
      })
      placedOrderForQuoteCookie.value = getCartId.value
      return data?.data?.placeOrder
    }
    catch (placeOrderException: any) {
      console.error('placeOrderException', placeOrderException)
    }

    pending.value = false
  }

  const {
    mutate: restoreCartMutation,
  } = useMutation(
    restoreCartMutationQuery,
    { clientId: 'customer' },
  )

  const restoreCart = async (cartId: string) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true

    try {
      return await restoreCartMutation({
        cartId,
      })
    }
    catch (restoreCartException: any) {
      console.error('restoreCartException', restoreCartException)
      return null
    }
    finally {
      pending.value = false
    }
  }

  onMounted(() => {
    const { query } = useRoute()
    getCart({ token: query.masked_id || undefined })

    if (!watchersEnabled.value) {
      watchersEnabled.value = true

      watch(cart, (newVal, oldVal) => {
        if (!oldVal && newVal) {
          loaded.value = true
        }
      })

      watch(isLoggedIn, () => {
        getCart({ force: true })
      })
    }
  })

  return {
    cart,
    reset,
    email,
    cartId,
    getCartId,
    cartCookieName,
    pending,
    loaded,
    quantity,
    sumQuantity,
    getCart,
    addedToCart,
    errors,
    total,
    addToCart,
    updateCartItem,
    removeCartItem,
    skus,
    items,
    hasSuppressedErrors,
    getCartItemsWithErrors,
    hasCartItemErrors,
    shippingCosts,
    applyCouponToCart,
    removeCouponFromCart,
    shippingAddressOrBillingAddress,
    billingAddress,
    shippingAddress,
    addEmailAddress,
    addEmailAddressLoading,
    addEmailAddressCalled,
    addEmailAddressError,
    addBillingAddress,
    addBillingAddressLoading,
    addBillingAddressCalled,
    addBillingAddressError,
    addShippingAddress,
    addShippingAddressLoading,
    addShippingAddressCalled,
    addShippingAddressError,
    shippingMethods,
    selectedShippingMethod,
    addShippingMethod,
    addShippingMethodError,
    addShippingMethodLoading,
    addShippingMethodCalled,
    selectedPaymentMethod,
    addPaymentMethod,
    addPaymentMethodError,
    addPaymentMethodLoading,
    addPaymentMethodCalled,
    placeOrder,
    placeOrderError,
    placeOrderLoading,
    placeOrderCalled,
    crossSells,
    upSells,
    inactive,
    createEmptyCart,
    restoreCart,
    cartStatus,
    placedOrderForQuoteCookie,
  }
}
